import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Themed confirmation dialog — the EHR `credConfirm` design, ported 1:1 from the
 * V38 mockup. A bare card: the message, then a right-aligned Cancel + confirm
 * button row. No title bar. ESC and a backdrop click cancel; focus moves into
 * the dialog on open. Replaces raw `window.confirm` for destructive prompts.
 */
export function ConfirmDialog({
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = true,
  busy = false,
  altLabel,
  onAlt,
  onConfirm,
  onClose,
  focusConfirm = false,
  className,
  alert = false,
  hideCancel = false,
}: {
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as a destructive (red) action; false gives green. */
  danger?: boolean;
  busy?: boolean;
  /** Optional middle button, e.g. "Don't Save" in an unsaved-changes prompt (ehr#180). */
  altLabel?: string;
  onAlt?: () => void;
  onConfirm: () => void;
  onClose: () => void;
  /** ehr#375: the confirm button starts selected, so Enter saves ("Save changes" is the default). */
  focusConfirm?: boolean;
  /** ehr#482: an extra class on the card (the red duplicate phone / email pop-up). */
  className?: string;
  /** ehr#482: an alert (role=alertdialog), so a screen reader announces it at once. */
  alert?: boolean;
  /** ehr#482: one button only (the confirm one), when there is nothing to choose between. */
  hideCancel?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Caught on window in the capture phase and stopped there (ehr#333), so Esc closes only this
    // dialog and not the window under it, whose own Esc listeners sit on document.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    if (focusConfirm && okRef.current) okRef.current.focus();
    else ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, focusConfirm]);

  return (
    <div className="cred-confirm-overlay" onClick={onClose}>
      <div
        className={className ? `cred-confirm-box ${className}` : 'cred-confirm-box'}
        role={alert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        {/* A rich message (lists, links: ehr#179) can't sit inside a <p>. */}
        {typeof message === 'string' ? (
          <p className="cred-confirm-msg">{message}</p>
        ) : (
          <div className="cred-confirm-msg">{message}</div>
        )}
        <div className="cred-confirm-actions">
          {!hideCancel && (
            <button type="button" className="cred-confirm-cancel" onClick={onClose} disabled={busy}>
              {cancelLabel}
            </button>
          )}
          {altLabel && onAlt && (
            <button
              type="button"
              className="cred-confirm-cancel cred-confirm-alt"
              onClick={onAlt}
              disabled={busy}
            >
              {altLabel}
            </button>
          )}
          <button
            type="button"
            ref={okRef}
            className={`cred-confirm-ok${danger ? ' danger' : ''}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
